import React from 'react';
import { Helmet } from 'react-helmet';

const AdvancedSEO = ({ 
  title, 
  description, 
  canonical, 
  pageType = 'website',
  schemaData = null,
  lastModified = '2025-01-17',
  additionalMeta = {}
}) => {
  // Default schema for all pages
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": canonical,
    "inLanguage": "en-US",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Legal Document Specialist",
      "url": "https://legaldocspecialist.com"
    },
    "dateModified": lastModified,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://legaldocspecialist.com"
        }
      ]
    }
  };

  // Merge with custom schema if provided
  const finalSchema = schemaData ? { ...defaultSchema, ...schemaData } : defaultSchema;

  return (
    <Helmet>
      {/* Enhanced Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={pageType} />
      <meta property="og:site_name" content="Legal Document Specialist" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:site" content="@legaldocspec" />
      
      {/* Additional Meta Tags */}
      <meta name="last-modified" content={lastModified} />
      <meta name="content-language" content="en-US" />
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content="California" />
      
      {/* Additional custom meta tags */}
      {Object.entries(additionalMeta).map(([key, value]) => (
        <meta key={key} name={key} content={value} />
      ))}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalSchema)}
      </script>
      
      {/* Preload critical resources */}
      <link rel="preload" href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800;900&family=Source+Sans+Pro:wght@300;400;500;600;700&display=swap" as="style" />
      
      {/* DNS prefetch for external resources */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
    </Helmet>
  );
};

export default AdvancedSEO;